<template>
	<Skeleton :paragraph="{ rows: 14 }" :title="false" :loading="loading">
		<Table
			aria-label="atmospheric message rows"
			:columns="columns"
			:data-source="cssrMessages"
			:row-selection="rowSelection"
			:pagination="false"
			size="small"
		>
			<template #bodyCell="{ column, record }">
				<template v-if="column.dataIndex === 'period'">
					<Select
						class="ui-select"
						v-model:value="record.period"
						:options="cssrIntervalsOptions"
						:disabled="disabled"
						size="small"
					/>
				</template>
				<template v-else-if="column.dataIndex === 'label'">
					{{ $t(`settings.sub-networks.cssrMessagesForm.cssrMessages.description.${record.label}`, $t('settings.sub-networks.cssrMessagesForm.cssrMessages.description.unknown')) }}
				</template>
			</template>
		</Table>
		<div class="pd-md-vertical">
			<TypographyText class="display-block">{{ selectedCountDisplay }}</TypographyText>
		</div>
		<Button class="display-block pd-vertical" type="primary" @click="onSubmit" :loading="isSubmitting" :disabled="settings.deleted || disabled">
			{{ $t('settings.vrsSettingsForm.buttonLabel') }}
		</Button>
	</Skeleton>
</template>

<script>
import { toRef, ref, watch, computed, nextTick } from 'vue';
import { TypographyText, Skeleton, Button, Table, Select, message } from 'ant-design-vue';

import { useI18n } from 'vue-i18n';

import Api from '@/api/Api';

import Consts from '@/api/Consts';
import Validators from '@/api/Validators';

import { showErrorMessage } from '@/components/common/errorMessage';
import { useProvideInjectFormChangedStatuses } from '@/utils/useProvideInject';
import { useLicenseStore } from '@/stores/licenseStore';

const GP_ONLY_CSSR_MESSAGE_CODES = new Set([
	'4073:8',
	'4073:9',
	'4073:10',
	'4091:4073:8',
	'4091:4073:9',
	'4091:4073:10',
]);

export default {
	components: {
		TypographyText, Skeleton, Table, Button, Select,
	},
	props: {
		loading: {},
		status: {},
		settings: {},
		cssrFilters: {},
		fetchSettings: {},
		disabled: {
			type: Boolean,
			default: false,
		},
	},
	setup(props) {
		const { t } = useI18n();
		const cssrMessages = ref([]);
		const selectedRowKeys = ref([]);

		const { injectValue } = useProvideInjectFormChangedStatuses;
		const formChangedStatuses = injectValue();
		const { enableWatch, pauseWatch, resetChangedState } = Validators.useWatchFormChanges(
			[cssrMessages, selectedRowKeys],
			formChangedStatuses,
			'CssrFiltersForm',
		);

		const statusDataRef = toRef(props, 'status');
		const cssrFiltersRef = toRef(props, 'cssrFilters');
		const settingsRef = toRef(props, 'settings');
		const licenseStore = useLicenseStore();

		const allCssrMessages = computed(() => statusDataRef.value.allCssrMessages || []);
		const isGpEnabled = computed(() => (
			licenseStore.allowPppGp && Boolean(settingsRef.value?.cssrGpEnabled)
		));
		const visibleCssrMessages = computed(() => {
			if (isGpEnabled.value) {
				return allCssrMessages.value;
			}
			return allCssrMessages.value.filter(item => !GP_ONLY_CSSR_MESSAGE_CODES.has(item.code));
		});
		const cssrIntervalsOptions = computed(() => {
			return Object.entries(Consts.CSSR_UPDATE_INTERVALS).map(([key, seconds]) => ({
				value: Number(key),
				label: t('settings.vrsSettingsForm.cssrMessagesFilters.period.seconds', Number(seconds)),
			})).sort((a, b) => a.value - b.value);
		});

		const updateCssrMessages = () => {
			pauseWatch();
			let selectedIds = [];
			cssrMessages.value = visibleCssrMessages.value.map(item => {
				const initialItem = cssrFiltersRef.value.cssr_messages_filters.find(initialItem => initialItem.code === item.code);
				if (initialItem != null && initialItem.checked) {
					selectedIds.push(item.code);
				}
				return {
					...item,
					key: item.code,
					period: (initialItem != null) ? Number(initialItem.period) : 2,
				}
			});
			selectedRowKeys.value = selectedIds;
			nextTick(enableWatch);
		};

		watch([statusDataRef, cssrFiltersRef, visibleCssrMessages], () => {
			updateCssrMessages();
		}, { immediate: true });

		const onSelect = keys => {
			selectedRowKeys.value = keys;
		};
		const rowSelection = computed(() => ({
			selectedRowKeys: selectedRowKeys.value,
			onChange: onSelect,
			getCheckboxProps: () => ({
				disabled: props.disabled,
			}),
		}));

		const isSubmitting = ref(false);
		const onSubmit = () => {
			let data = [];
			for (const item of cssrMessages.value) {
				if (selectedRowKeys.value.length === 0 || selectedRowKeys.value.includes(item.key)) {
					data.push({
						code: item.key,
						period: item.period,
						checked: selectedRowKeys.value.includes(item.key),
					});
				}
			}
			isSubmitting.value = true;
			Api.cssrMountpointVrsUpdate(cssrFiltersRef.value.id, { cssr_messages_filters: data }, response => {
				isSubmitting.value = false;
				if (response.status === 'ok') {
					props.fetchSettings(() => { resetChangedState() }, true);
					message.success(t('settings.vrsSettingsForm.messages.success'));
				} else {
					showErrorMessage(response.errors);
				}
			});
		}

		const selectedCountDisplay = computed(() => {
			if (selectedRowKeys.value.length === 0 || selectedRowKeys.value.length === visibleCssrMessages.value.length) {
				return t('settings.vrsSettingsForm.cssrMessagesFilters.filters.allMessages');
			}
			return t('settings.vrsSettingsForm.cssrMessagesFilters.filters.selectedMessages', selectedRowKeys.value.length);
		});
		return {
			cssrMessages,
			selectedRowKeys,
			rowSelection,
			isSubmitting,
			onSubmit,
			cssrIntervalsOptions,
			columns: Consts.cssrMessagesFiltersTableColumnsComp,
			selectedCountDisplay,
		};
	},
}
</script>

<style scoped>
.ui-select {
	width: 130px;
}
</style>
